/**
 * A data-layer error modeled as data (not a thrown exception), so hooks can carry
 * it alongside their value and consumers can render it inline with a retry. The
 * backend's native error code is mapped to a {@link JcDataErrorCategory} at the
 * single backend boundary (e.g. `BaseFirestoreService`); the raw cause never leaks.
 */
export enum JcDataErrorCategory {
    /** Offline / timeout / overload — retrying may succeed. */
    TRANSIENT = 'transient',
    /** Denied by rules or unauthenticated — retrying as-is won't help. */
    PERMISSION = 'permission',
    /** Anything else. */
    UNKNOWN = 'unknown',
}

export interface JcDataError {
    readonly category: JcDataErrorCategory;
    /** True only for {@link JcDataErrorCategory.TRANSIENT}. */
    readonly retryable: boolean;
    /** The original error, for logging. */
    readonly cause: unknown;
}

/** Builds a {@link JcDataError}; `retryable` is derived from the category. */
export const jcDataError = (category: JcDataErrorCategory, cause: unknown): JcDataError => ({
    category,
    retryable: category === JcDataErrorCategory.TRANSIENT,
    cause,
});
